import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { Linkedin, Twitter, Github, Instagram, Mail, Phone, MapPin, Star } from 'lucide-react';
import logoImage from '../data/d.png';
import { Link } from 'react-router-dom';

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 4L20 20M20 4L4 20" />
  </svg>
);

const Footer = () => {
  const { t } = useTranslation();

  const footerLinks = [
    { key: 'footer.links.about', href: '#about' },
    { key: 'footer.links.services', href: '#services' },
    { key: 'footer.links.portfolio', href: '#portfolio' },
    { key: 'footer.links.contact', href: '#contact' },
    { key: 'footer.links.privacy', href: '/privacy', isRoute: true },
    { key: 'footer.links.terms', href: '/terms', isRoute: true },
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: Linkedin, href: 'https://www.linkedin.com/company/saham-systems-holding-for-information-technology' },
    { name: 'Twitter', icon: XIcon, href: 'https://x.com/saham_holding' },
    { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/saham_holding' },
  ];

  const contactInfo = [
    { icon: Mail, text: 'info@saham-holding.com', href: 'mailto:info@saham-holding.com' },
    { icon: Phone, text: '+966-533040509', href: 'tel:+966533040509' },
    { icon: MapPin, text: t('contact.info.address'), href: 'https://maps.app.goo.gl/TQZsCX3pz3ZsC2d57' },
  ];

  return (
    <footer className="bg-black dark:bg-black text-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-gradient-to-r from-neon-green/10 to-neon-cyan/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-gradient-to-r from-neon-blue/10 to-neon-purple/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Enhanced Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 rtl:space-x-reverse mb-6">
              <div className="relative">
                <img
                  src={logoImage}
                  alt="Saham Holding - Innovative Technology Partner"
                  className="w-12 h-12 object-contain"
                />
                <div className="absolute -inset-2 bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 rounded-full blur-lg opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-black gradient-text-neon">{t('nav.brand')}</span>
                <span className="text-sm text-neon-green/80 font-medium">{t('footer.tagline')}</span>
              </div>
            </div>
            <p className="text-white/70 mb-8 max-w-lg text-lg leading-relaxed">
              {t('footer.description')}
            </p>

            {/* Enhanced Social Links */}
            <div className="flex space-x-4 rtl:space-x-reverse mb-8">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-12 h-12 bg-black/30 backdrop-blur-sm border border-neon-green/30 rounded-xl flex items-center justify-center hover:border-neon-green/50 hover:bg-neon-green/10 transition-all duration-300 shadow-lg hover:shadow-neon-green/20"
                  title={social.name}
                >
                  <social.icon className="w-6 h-6 text-neon-green" />
                </motion.a>
              ))}
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              {contactInfo.map((contact, index) => (
                <motion.a
                  key={index}
                  href={contact.href}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center space-x-3 rtl:space-x-reverse text-white/70 hover:text-neon-green transition-colors duration-300 group"
                  {...(contact.icon === MapPin ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <contact.icon className="w-4 h-4 text-neon-green" />
                  </div>
                  <span className="text-sm font-medium">{contact.text}</span>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Enhanced Quick Links */}
          <div>
            <h3 className="text-xl font-black text-white mb-6 flex items-center">
              <Star className="w-5 h-5 text-neon-green mr-2" />
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-4">
              {footerLinks.slice(0, 4).map((link, index) => (
                <li key={index}>
                  <motion.a
                    href={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 5 }}
                    className="text-white/70 hover:text-neon-green transition-all duration-300 font-medium group flex items-center"
                  >
                    <div className="w-2 h-2 bg-neon-green rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {t(link.key)}
                  </motion.a>
                </li>
              ))}
            </ul>
          </div>

          {/* Enhanced Legal Links */}
          <div>
            <h3 className="text-xl font-black text-white mb-6 flex items-center">
              <Star className="w-5 h-5 text-neon-cyan mr-2" />
              {t('footer.legal')}
            </h3>
            <ul className="space-y-4">
              {footerLinks.slice(4).map((link, index) => (
                <li key={index}>
                  {link.isRoute ? (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ x: 5 }}
                      className="text-white/70 hover:text-neon-cyan transition-all duration-300 font-medium group flex items-center"
                    >
                      <div className="w-2 h-2 bg-neon-cyan rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <Link to={link.href}>{t(link.key)}</Link>
                    </motion.div>
                  ) : (
                    <motion.a
                      href={link.href}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ x: 5 }}
                      className="text-white/70 hover:text-neon-cyan transition-all duration-300 font-medium group flex items-center"
                    >
                      <div className="w-2 h-2 bg-neon-cyan rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      {t(link.key)}
                    </motion.a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Enhanced Bottom Bar */}
        <div className="border-t border-neon-green/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-white/60 text-sm font-medium"
          >
            {t('footer.copyright')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex items-center space-x-4 rtl:space-x-reverse mt-4 md:mt-0"
          >
            <span className="text-white/60 text-sm font-medium">{t('footer.madeWith')}</span>
            <div className="w-4 h-4 bg-gradient-to-r from-neon-green to-neon-cyan rounded-full animate-pulse"></div>
            <span className="text-white/60 text-sm font-medium">{t('footer.bySaham')}</span>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 